import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  MessageCircle, Users, DollarSign, Send, ArrowLeft,
  Home, Ticket, Heart, Settings, Share2, SkipBack, SkipForward
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TipArtistSheet } from '@/components/modals/TipArtistSheet';
import { 
  mockEvents, mockEventChatMessages, getDebugUI, mockArtists 
} from '@/lib/mockData';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';

// Recap chat uses different accent color (purple/violet instead of primary/orange)
const RECAP_CHAT_ACCENT = 'text-violet-400';
const RECAP_BADGE_COLOR = 'bg-violet-600';

// Mobile Bottom Navigation
const MobileBottomNav = ({ activePath }) => {
  const navItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: Ticket, label: 'Tickets', path: '/me/tickets' },
    { icon: Heart, label: 'Wishlist', path: '/me/wishlist' },
    { icon: Settings, label: 'Settings', path: '/me/settings' },
  ];

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-card/95 backdrop-blur-sm border-t border-border pb-safe">
      <div className="flex items-center justify-around h-16">
        {navItems.map(item => (
          <Link 
            key={item.path} 
            to={item.path}
            className={`flex flex-col items-center gap-1 px-4 py-2 ${
              activePath === item.path ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <item.icon className="w-5 h-5" />
            <span className="text-xs">{item.label}</span>
          </Link>
        ))}
      </div>
    </nav>
  );
};

export function UserEventRecap() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const event = mockEvents.find(e => e.id === eventId) || mockEvents[2]; // Default to past event
  
  const isDebugMode = getDebugUI();
  const storageKey = `exit88_recap_chat_${eventId}`;
  
  // Video state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [progress, setProgress] = useState(35);
  
  // Chat state - load from localStorage in debug mode
  const [chatMessages, setChatMessages] = useState(() => {
    if (isDebugMode) {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          console.error('Failed to parse stored chat messages');
        }
      }
    }
    // Seed with mock recap chat messages (different from live)
    return mockEventChatMessages[eventId] || mockEventChatMessages['evt-006'] || [];
  });
  
  const [newMessage, setNewMessage] = useState('');
  const [showChat, setShowChat] = useState(true);
  const [showTipSheet, setShowTipSheet] = useState(false);
  const chatContainerRef = useRef(null);
  const videoContainerRef = useRef(null);

  // Persist chat messages to localStorage in debug mode
  useEffect(() => {
    if (isDebugMode && chatMessages.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(chatMessages));
    }
  }, [chatMessages, isDebugMode, storageKey]);

  // Scroll chat to bottom when messages update
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const handleSendMessage = useCallback(() => {
    if (!newMessage.trim()) return;
    
    const msg = {
      id: `msg-recap-${Date.now()}`,
      userId: 'user-001',
      userName: 'You',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50',
      message: newMessage.trim(),
      timestamp: new Date().toISOString(),
      isBlocked: false,
      isOwn: true,
      isRecap: true // Flag for recap styling
    };
    
    setChatMessages(prev => [...prev, msg]);
    setNewMessage('');
    toast.success('Comment posted');
  }, [newMessage]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement && videoContainerRef.current) {
      videoContainerRef.current.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  // Handle tip sent - add system message to chat
  const handleTipSent = (tip) => {
    const systemMsg = {
      id: `tip-${Date.now()}`,
      userId: 'system',
      userName: 'System',
      avatar: '/exit88-logo.png',
      message: `💰 ${tip.senderName || 'A viewer'} sent $${tip.amount} to ${tip.artistName}!`,
      timestamp: new Date().toISOString(),
      isSystem: true,
      isBlocked: false
    };
    setChatMessages(prev => [...prev, systemMsg]);
    toast.success(`Thanks for supporting ${tip.artistName}!`);
  };

  return (
    <div className="h-screen bg-black overflow-hidden" data-testid="user-event-recap">
      {/* Debug Mode Indicator */}
      {isDebugMode && (
        <div className="fixed top-2 left-1/2 -translate-x-1/2 z-50 bg-violet-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
          DEBUG MODE (Recap)
        </div>
      )}

      {/* === MOBILE LAYOUT: Video fullscreen with chat overlay === */}
      <div className="lg:hidden relative h-full">
        {/* Video Background - Full screen */}
        <div className="absolute inset-0">
          <img 
            src={event.coverImage || event.image} 
            alt={event.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          
          {/* Play overlay when paused */}
          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center">
              <button 
                className="w-20 h-20 rounded-full bg-violet-600/90 flex items-center justify-center hover:scale-110 transition-transform"
                onClick={() => setIsPlaying(true)}
              >
                <Play className="w-10 h-10 text-white ml-1" fill="currentColor" />
              </button>
            </div>
          )}
        </div>

        {/* RECAP Badge & View Count - Top */}
        <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
          <span className={`px-2 py-1 ${RECAP_BADGE_COLOR} text-white text-xs font-bold rounded`}>RECAP</span>
          <span className="px-2 py-1 bg-black/60 text-white text-xs rounded flex items-center gap-1">
            <Users className="w-3 h-3" /> {event.views?.toLocaleString() || '12,458'}
          </span>
        </div>

        {/* Back Button - Top Right */}
        <button 
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 z-10 p-2 bg-black/40 rounded-full text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        {/* Chat Overlay - LEFT side, above video */}
        <div className="absolute bottom-44 left-0 right-20 max-h-[30vh] flex flex-col z-10">
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto px-4 space-y-2 scrollbar-hide"
            data-testid="chat-messages-mobile"
          >
            {chatMessages.slice(-10).map((msg) => (
              <div 
                key={msg.id} 
                className={`${msg.isSystem ? 'bg-gradient-to-r from-yellow-500/30 to-orange-500/30 rounded-lg px-3 py-1.5' : ''}`}
              >
                {msg.isSystem ? (
                  <span className="text-sm text-yellow-300">{msg.message}</span>
                ) : (
                  <div className="flex gap-2 items-start">
                    <img 
                      src={msg.avatar} 
                      alt={msg.userName}
                      className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                    />
                    <div>
                      {/* Recap comments use violet accent, live comments use primary */}
                      <span className={`text-xs font-medium mr-2 ${msg.isRecap ? RECAP_CHAT_ACCENT : 'text-primary'}`}>
                        {msg.userName}
                      </span>
                      <span className="text-white text-sm">{msg.message}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons - RIGHT side */}
        <div className="absolute bottom-48 right-4 flex flex-col gap-4 z-10">
          <button className="flex flex-col items-center">
            <div className="w-10 h-10 bg-black/40 rounded-full flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <span className="text-xs text-white mt-1">4.2K</span>
          </button>
          <button className="flex flex-col items-center" onClick={() => setShowChat(!showChat)}>
            <div className="w-10 h-10 bg-black/40 rounded-full flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <span className="text-xs text-white mt-1">Chat</span>
          </button>
          <button className="flex flex-col items-center">
            <div className="w-10 h-10 bg-black/40 rounded-full flex items-center justify-center">
              <Share2 className="w-5 h-5 text-white" />
            </div>
            <span className="text-xs text-white mt-1">Share</span>
          </button>
        </div>

        {/* Video Progress Bar */}
        <div className="absolute bottom-32 left-4 right-4 z-10">
          <div className="w-full h-1 bg-white/30 rounded-full cursor-pointer">
            <div 
              className="h-full bg-violet-500 rounded-full relative"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-violet-500 rounded-full" />
            </div>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-white/70">12:45</span>
            <span className="text-xs text-white/70">35:20</span>
          </div>
        </div>

        {/* Bottom Bar - Chat Input + Tip Button - with safe area spacing */}
        <div className="absolute bottom-0 left-0 right-0 p-4 pb-28 z-10 bg-gradient-to-t from-black/90 to-transparent pt-8">
          <div className="flex gap-2 items-center">
            <div className="flex-1 relative">
              <Input 
                type="text"
                placeholder="Add a comment..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 pr-10 h-10"
                data-testid="chat-input-mobile"
              />
              <button 
                onClick={handleSendMessage}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-violet-400"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <Button 
              onClick={() => setShowTipSheet(true)}
              className="h-10 px-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold"
              data-testid="tip-artist-btn-mobile"
            >
              <DollarSign className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* === DESKTOP LAYOUT: Split view with chat on left, video on right === */}
      <div className="hidden lg:flex h-full w-full">
        {/* Chat Sidebar - LEFT on desktop */}
        <div 
          className="flex flex-col w-96 bg-card border-r border-border"
          data-testid="chat-sidebar"
        >
          {/* Chat Header */}
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-white">Recap Comments</h3>
              <p className="text-xs text-muted-foreground truncate max-w-[200px]">{event.title}</p>
            </div>
            <span className={`px-2 py-1 ${RECAP_BADGE_COLOR} text-white text-xs font-bold rounded`}>RECAP</span>
          </div>

          {/* Chat Messages */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-4 space-y-3"
            data-testid="chat-messages"
          >
            {chatMessages.map((msg) => (
              <div key={msg.id} className={`flex gap-2 ${msg.isOwn ? 'flex-row-reverse' : ''} ${msg.isSystem ? 'justify-center' : ''}`}>
                {msg.isSystem ? (
                  <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 px-4 py-2 rounded-full">
                    <span className="text-sm text-yellow-300">{msg.message}</span>
                  </div>
                ) : (
                  <>
                    <img 
                      src={msg.avatar} 
                      alt={msg.userName}
                      className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                    />
                    <div className={`flex-1 ${msg.isOwn ? 'text-right' : ''}`}>
                      <div className={`flex items-baseline gap-2 ${msg.isOwn ? 'justify-end' : ''}`}>
                        {/* Recap comments use violet accent */}
                        <span className={`text-sm font-medium ${msg.isRecap ? RECAP_CHAT_ACCENT : 'text-primary'}`}>
                          {msg.userName}
                        </span>
                      </div>
                      <p className={`text-sm text-white ${msg.isOwn ? 'bg-violet-500/20 inline-block px-3 py-1 rounded-lg' : ''}`}>
                        {msg.message}
                      </p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Chat Input + Actions */}
          <div className="p-4 border-t border-border space-y-3">
            <div className="flex gap-2">
              <Input 
                type="text"
                placeholder="Add a comment..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 bg-secondary border-border text-white placeholder:text-muted-foreground"
                data-testid="chat-input"
              />
              <Button 
                onClick={handleSendMessage}
                className="bg-violet-500 hover:bg-violet-600 text-white px-3"
                data-testid="send-message-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>

            {/* Tip Button */}
            <Button 
              onClick={() => setShowTipSheet(true)}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold h-11"
              data-testid="tip-artist-btn"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Tip Artist
            </Button>
          </div>
        </div>

        {/* Video Player Area - RIGHT side on desktop */}
        <div 
          ref={videoContainerRef}
          className="relative flex-1"
        >
          {/* Video Background / Placeholder */}
          <div className="absolute inset-0">
            <img 
              src={event.coverImage || event.image} 
              alt={event.title}
              className="w-full h-full object-cover opacity-80"
            />
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <button 
                  className="w-20 h-20 rounded-full bg-violet-600/90 flex items-center justify-center hover:scale-110 transition-transform"
                  onClick={() => setIsPlaying(true)}
                  data-testid="play-button"
                >
                  <Play className="w-10 h-10 text-white ml-1" fill="currentColor" />
                </button>
              </div>
            )}
          </div>

          {/* Top Bar - Back button and badges */}
          <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10 bg-gradient-to-b from-black/60 to-transparent">
            <button 
              onClick={() => navigate(-1)} 
              className="p-2 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 ${RECAP_BADGE_COLOR} text-white text-xs font-semibold rounded-full flex items-center gap-1`} data-testid="recap-badge">
                EVENT RECAP
              </span>
              <span className="px-3 py-1 bg-black/50 text-white text-xs rounded-full flex items-center gap-1" data-testid="view-count">
                <Users className="w-3.5 h-3.5" />
                {(event.views || 12458).toLocaleString()} views
              </span>
            </div>
          </div>

          {/* Bottom Controls */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent z-10">
            {/* Progress bar */}
            <div className="w-full h-1 bg-white/30 rounded-full mb-4 cursor-pointer">
              <div 
                className="h-full bg-violet-500 rounded-full relative"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-violet-500 rounded-full" />
              </div>
            </div>

            <div className="flex items-center justify-between gap-4">
              {/* Left Controls */}
              <div className="flex items-center gap-3">
                {/* Skip back */}
                <button className="p-2 text-white hover:text-violet-400">
                  <SkipBack className="w-5 h-5" />
                </button>
                <button 
                  className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                  onClick={() => setIsPlaying(!isPlaying)}
                  data-testid="play-pause-btn"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </button>
                {/* Skip forward */}
                <button className="p-2 text-white hover:text-violet-400">
                  <SkipForward className="w-5 h-5" />
                </button>
                <button 
                  className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                  onClick={() => setIsMuted(!isMuted)}
                  data-testid="mute-btn"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                {/* Time */}
                <span className="text-white text-sm">12:45 / 35:20</span>
              </div>

              {/* Right Controls */}
              <div className="flex items-center gap-2">
                <button 
                  className="flex w-10 h-10 rounded-full bg-white/20 items-center justify-center text-white hover:bg-white/30 transition-colors"
                  onClick={toggleFullscreen}
                  data-testid="fullscreen-btn"
                >
                  {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav activePath="/events" />

      {/* Tip Artist Sheet */}
      <TipArtistSheet 
        isOpen={showTipSheet}
        onClose={() => setShowTipSheet(false)}
        eventId={eventId}
        eventArtists={event.artists || []}
        onTipSent={handleTipSent}
      />
    </div>
  );
}

export default UserEventRecap;
